import { PropertyValues, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksScrollEndEvent } from '../../events';
import { BlocksElement } from '../BlocksElement';
import type { DropdownPanelSize, DropdownPanelVariant, DropdownPlacement } from './dropdown-panel.model';
import styles from './dropdown-panel.styles';

/**
 * @slot header - The component header content
 * @slot default - The component content body
 * @slot footer - The component footer content
 * @fires bl-scroll-end - emitted according to scrollOffset  values, by default it is zero so emitted at the end of dropdown panel.
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-header - The component's header.
 * @csspart body - The component's body wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart panel-footer - The component's footer slot.
 */
export class BlocksDropdownPanel extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Adds a max height and scrolling of the panel
   */
  @property({ type: Boolean })
  hasScroll: boolean | undefined;

  /**
   * Changes styling on panel for variant with header
   */
  @property({ type: Boolean })
  hasHeader = false;

  /**
   * Changes styling on panel for variant with footer
   */
  @property({ type: Boolean })
  hasFooter = false;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**bare** removes border from dropdown panel</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: DropdownPanelVariant;

  @property({ type: String })
  placement: DropdownPlacement;

  /**
   * Property to track the list item empty status. This will be used to hide the empty panel when the list item is empty.
   */
  @property({ type: Boolean })
  isEmpty = false;

  /**
   * Size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the dropdown height with label inside</li>
   * <li>**md** reduce the dropdown height with label outside </li>
   * <li>**sm** reduce the dropdown height by removing label </li>
   * </ul>
   * </cat-text-passage>
   * The padding values (through properties) are set in dropdown.scss file for the dropdown panel component and now this is moving to overlay (to the body) then the corresponding CSS will not be available in panel component and currently the padding CSS moves to dropdown panel and enabling the CSS based on the size condition in panel component.
   */

  @property({ type: String })
  size: DropdownPanelSize = 'lg';

  /**
   * Specifies the bottom offset for the dropdown panel to determine when lazy loading should be triggered.
   * The value is a string representing the bottom offset in the format 'px' or '%'.
   * Default value is '0px'.
   */
  @property({ type: String })
  scrollOffset = '0px';

  /**
   * Query the drop down panel body
   */
  @query('.cat-c-dropdown-panel__body')
  private panelBody!: HTMLElement;

  /**
   * Query the marker element from body
   */
  @query('.cat-c-panel')
  private panelMarkerElement!: HTMLElement;

  //instance of intersection observer,which will be used later to disconnect after use.
  private _observer: IntersectionObserver | null = null;

  protected slots = new SlotController(this, null, 'header', 'footer');

  constructor() {
    super();
    this.addEventListener('bl-focus-last-item', this.keyboardLastItemFocus);
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    requestAnimationFrame(() => {
      this.configureScrollObserver();
    });
  }

  /**
   * 1. Select the panel body element inside the shadow DOM
   * 2. Define options for IntersectionObserver: 
          - root: the element to observe (the panel body)
          - threshold: percentage of visibility (0 means any intersection triggers)
          - rootMargin: margin around the root to expand or shrink the observable area
   * 3. Create the IntersectionObserver instance
       - This observer will track when an element is intersecting with the panel body.
   * 4. Check if the element is intersecting (i.e., if it's in the viewport)
   * 5. Check if the panel body has enough scrollable content
   * 6. Dispatch a custom event if the panel has scrollable content and is at the bottom
   * 7. Start observing the target element ('cat-c-panel') to detect when it's in the view
   */
  private configureScrollObserver() {
    const bottomOffset = this.negateOffset(this.scrollOffset);
    /*2*/
    const options = {
      root: this.panelBody,
      threshold: 0,
      rootMargin: `0px 0px ${bottomOffset} 0px`
    };

    /*3*/
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        /*4*/
        if (entry.isIntersecting) {
          /*5*/
          const hasScroll = this.panelBody ? this.panelBody.scrollHeight > this.panelBody.clientHeight : false;
          /*6*/
          if (hasScroll) {
            // Dispatch 'bl-scroll-end' event when scrolling reaches the end
            this.dispatch({ eventName: 'bl-scroll-end', detailObj: {} }) as BlocksScrollEndEvent;
          }
        }
      });
    }, options);

    /*7*/
    observer.observe(this.panelMarkerElement);
    this._observer = observer;
  }

  /**
   *
   * @param event If offset is more than zero and using keyboard user tries to focus from first to last element then manually triggering this event.
   */
  public keyboardLastItemFocus() {
    if (this.scrollOffset !== '0px' && this.scrollOffset !== '0%') {
      this.dispatch({ eventName: 'bl-scroll-end', detailObj: {} }) as BlocksScrollEndEvent;
    }
  }
  /**
   * Helper function to negate the offSet value conditionally
   * 1.If offset is 0px or 0%, do not negate
   * 2.Otherwise, negate the value (works for both px and % values)
   */
  private negateOffset(offset: string): string {
    if (offset === '0px' || offset === '0%') {
      return offset; /*1*/
    }
    /*2*/
    return `-${offset}`;
  }
  /**
   * Checks if the scrollOffset is non-zero, returning true for any value other than '0px' or '0%', and false for those zero values.
   */
  private isOffsetValueGreaterThanZero(): boolean {
    if (this.scrollOffset === '0px' || this.scrollOffset === '0%') {
      return false;
    }
    return true;
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this.removeEventListener('bl-focus-last-item', this.keyboardLastItemFocus);
  }

  render(): TemplateResult<1> {
    if (this.isEmpty) {
      return html`${nothing}`;
    }
    const componentClassNames = this.componentClassNames('cat-c-dropdown-panel cat-c-dropdown--large', {
      'cat-c-dropdown-panel--bare': this.variant === 'bare',
      'cat-has-header': this.hasHeader === true,
      'cat-has-footer': this.hasFooter === true,
      'cat-has-scroll': this.hasScroll === true,
      'cat-c-dropdown--large': this.size === 'lg',
      'cat-c-dropdown--medium': this.size === 'md',
      'cat-c-dropdown--small': this.size === 'sm',
      'cat-c-dropdown-panel--has-header': this.slots.hasSlotted('header'),
      'cat-c-dropdown-panel--has-footer': this.slots.hasSlotted('footer')
    });
    const applyAbsolutePosition = this.isOffsetValueGreaterThanZero();
    return html`
      <div part="base" class="${componentClassNames}">
        <div part="header-wrapper" class="cat-c-dropdown-panel__header">
          <slot part="part-header" name="header"></slot>
        </div>
        <div part="body" class="cat-c-dropdown-panel__body">
          <slot part="part-default"></slot>
          <div class="cat-c-panel ${applyAbsolutePosition ? 'cat-c-position' : ''}"></div>
        </div>
        <div part="footer-wrapper" class="cat-c-dropdown-panel__footer">
          <slot part="panel-footer" name="footer"></slot>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-dropdown-panel') === undefined) {
  customElements.define('cat-dropdown-panel', BlocksDropdownPanel);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-dropdown-panel': BlocksDropdownPanel;
  }
}
